import { IPCheckbox, IPLabel, IPSpinButton } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import ComboBoxItemCreator from 'components/ComboBoxItemCreator/ComboBoxItemCreator';
import ExpressionEditor, { ExpressionValidationState } from 'components/KPI/Expression/ExpressionEditor';
import { ExpressionReference } from 'components/KPI/Expression/expressionEditor.helpers';
import TextInfo from 'components/Notification/Info';
import LocalizedDatePicker from 'components/Pickers/LocalizedDatePicker';
import InlineSuggestions from 'components/Suggestions/InlineSuggestions';
import RichTextEditorMCE from 'components/Text/RichTextEditorMCE';
import { globalKPIDecimal18c8 } from 'globalConstants';
import { globalStackTokensGapSmall } from 'globalStyles';
import { ComboBoxItem, ComboBoxItemList } from 'models/combobox/ComboBoxItem';
import KPI, { KPITypes } from 'models/kpi/kpi';
import StringValue, { StringValueTypes } from 'models/stringValue/stringValue';
import { StringValue_Translation } from 'models/stringValue/stringValue_Translation';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addDateDays, getDateDiffDays } from 'utils/datetime';
import { newGuid } from 'utils/guid';
import { isEmpty } from 'utils/string';
import { filterFormulaOperands } from './kpiDetailsForm.helpers';
import { validateDefMaxValue, validateDefMinValue, validateMaxValue, validateMinValue } from './KPIHelpers';

interface IKPITypeConfigRendererProps {
  kpi: KPI;
  // All KPIs, used by the expression editor to offer/validate formula operands. Optional: without
  // it the Expression type falls back to a plain validated input.
  kpis?: KPI[];
  formKpiIds?: number[];
  originalExpression?: string;
  expressionValidationUpdated?: (state: ExpressionValidationState) => void;
  defValueUpdated: (defValue: string | undefined) => void;
  minValueUpdated: (minValue: string | undefined) => void;
  maxValueUpdated: (maxValue: string | undefined) => void;
  requiredUpdated: (required: boolean) => void;
  choicesUpdated: (choices: StringValue[] | undefined, defvalue: string | undefined) => void;
  decimalCountUpdated: (decimalCount: number | undefined) => void;
  showLabelUpdated: (showLabel: boolean) => void;
}

const KPITypeConfigRenderer = (props: IKPITypeConfigRendererProps) => {
  //
  // Main render
  //
  if (!props.kpi || props.kpi.type === KPITypes.NotSet) {
    return null;
  }

  switch (props.kpi.type) {
    case KPITypes.TextMultiline:
    case KPITypes.Text:
    case KPITypes.Html:
      return <KPITypeConfigText {...props} />;
    case KPITypes.Number:
      return <KPITypeConfigNumber {...props} />;
    case KPITypes.SuccesError:
      return <KPITypeConfigSuccesError {...props} />;
    case KPITypes.Date:
      return <KPITypeConfigDate {...props} />;
    case KPITypes.Expression:
      return <KPITypeConfigExpression {...props} />;
    case KPITypes.Choice:
      return <KPITypeConfigChoice {...props} />;
    default:
      return null;
  }
};

export default KPITypeConfigRenderer;

//
// Expression
//
const KPITypeConfigExpression = (props: IKPITypeConfigRendererProps) => {
  const { t } = useTranslation(['translation', 'forms', 'kpi', 'kpis']);

  // Selectable operands: saved Number fields, scoped to the current form when a form context is
  // given (the Organization KPI page passes none and keeps the full pool), never the field itself.
  const references = useMemo<ExpressionReference[] | undefined>(
    () => {
      if (!props.kpis) return undefined;

      const selectableIds = new Set(
        filterFormulaOperands(props.kpis, props.formKpiIds, props.kpi.kpiId).map((kpi) => kpi.kpiId),
      );

      return props.kpis.flatMap<ExpressionReference>((kpi) =>
        kpi.kpiId > 0
          ? [
              {
                id: kpi.kpiId,
                name: kpi.name,
                isNumeric: kpi.type === KPITypes.Number,
                isSelectable: selectableIds.has(kpi.kpiId),
              },
            ]
          : [],
      );
    },
    [props.kpis, props.formKpiIds, props.kpi.kpiId],
  );
  const inputId = `kpi-expression-${props.kpi.kpiId || 'new'}`;
  const labelId = `${inputId}-label`;

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
      <IPStack.Item>
        {/* Learn-more link disabled: the article was not migrated to HubSpot (still draft).
            Restore with learnMoreLink={globalKB_howToWorkWithKPIExpressions} from globalConstants. */}
        <TextInfo message={t('kpis:Fields.ExpressionInfo')}>
          <IPLabel id={labelId} htmlFor={inputId}>
            {t('kpis:Fields.Expression')}
          </IPLabel>
        </TextInfo>
        <ExpressionEditor
          id={inputId}
          labelId={labelId}
          value={props.kpi.defValue ?? ''}
          originalValue={props.originalExpression ?? ''}
          maxLength={1024}
          references={references}
          onValidationStateChange={props.expressionValidationUpdated}
          onChange={(newValue) => props.defValueUpdated(newValue)}
        />
      </IPStack.Item>
      <IPStack.Item>
        <TextInfo message={t('kpis:Fields.DecimalCountInfo')}>
          <IPLabel>{t('kpis:Fields.DecimalCount')}</IPLabel>
        </TextInfo>
        <IPSpinButton
          value={props.kpi.decimalCount?.toString() ?? ''}
          min={0}
          max={8}
          onChange={(ev, newValue) => props.decimalCountUpdated(Number.parseInt(newValue ?? ''))}
          styles={{ root: { width: 150 } }}
        ></IPSpinButton>
      </IPStack.Item>
    </IPStack>
  );
};

//
// Text
//
const KPITypeConfigText = (props: IKPITypeConfigRendererProps) => {
  const { t } = useTranslation(['translation', 'forms', 'kpi']);

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
      <IPStack.Item>
        <TextInfo
          message={t('kpis:Fields.DefValueInfo', {
            entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
          })}
        >
          <IPLabel>{t('kpis:Fields.DefValue')}</IPLabel>
        </TextInfo>
        <IPTextField
          multiline
          value={props.kpi.defValue ?? ''}
          maxLength={1024}
          onChange={(ev, newValue) => props.defValueUpdated(newValue)}
          style={{
            resize: 'vertical',
            maxHeight: 200,
          }}
        ></IPTextField>
      </IPStack.Item>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <TextInfo
              message={t('kpis:Fields.MinValueTextInfo', {
                entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
              })}
            >
              <IPLabel>{t('kpis:Fields.MinValueText')}</IPLabel>
            </TextInfo>
            <IPSpinButton
              value={props.kpi.minValue?.toString() ?? ''}
              onIncrement={(newValue) => {
                if (!newValue) newValue = '0';
                const val = Number.parseInt(newValue) + 1;
                const max = 1024;
                if (val > max) {
                  return max.toString();
                }

                return val.toString();
              }}
              onDecrement={(newValue) => {
                if (!newValue) newValue = '1';
                const val = Number.parseInt(newValue) - 1;
                if (val < 0) {
                  return '0';
                }

                return val.toString();
              }}
              onValidate={(newValue) => {
                //normally empty values are discarded as not valid
                //also return empty strings to reset the field
                return newValue;
              }}
              onChange={(ev, newValue) => {
                if (newValue) {
                  newValue = validateMaxValue(props.kpi, props.kpi.minValue, newValue);
                  newValue = newValue && Number.parseInt(newValue) < 0 ? '0' : newValue;
                  props.minValueUpdated(newValue);
                } else {
                  props.minValueUpdated(undefined);
                }
              }}
              styles={{ root: { width: 150 } }}
            ></IPSpinButton>
          </IPStack.Item>
          <IPStack.Item>
            <TextInfo
              message={t('kpis:Fields.MaxValueTextInfo', {
                entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
              })}
            >
              <IPLabel>{t('kpis:Fields.MaxValueText')}</IPLabel>
            </TextInfo>
            <IPSpinButton
              value={props.kpi.maxValue?.toString() ?? ''}
              onIncrement={(newValue) => {
                if (!newValue) newValue = props.kpi.minValue?.toString() || '0';
                const val = Number.parseInt(newValue) + 1;
                const max = 1024;
                if (val > max) {
                  return max.toString();
                }

                return val.toString();
              }}
              onDecrement={(newValue) => {
                if (!newValue) newValue = '2';
                const val = Number.parseInt(newValue) - 1;
                if (val < 1) {
                  return '1';
                }

                return val.toString();
              }}
              onValidate={(newValue) => {
                //normally empty values are discarded as not valid
                //also return empty strings to reset the field
                return newValue;
              }}
              onChange={(ev, newValue) => {
                if (newValue) {
                  newValue = validateMinValue(props.kpi, props.kpi.maxValue, newValue);
                  newValue = newValue && Number.parseInt(newValue) > 1024 ? '1024' : newValue;
                  props.maxValueUpdated(newValue);
                } else {
                  props.maxValueUpdated(undefined);
                }
              }}
              styles={{ root: { width: 150 } }}
            ></IPSpinButton>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <TextInfo
          message={t('kpis:Fields.RequiredInfo', {
            entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
          })}
        >
          <IPLabel>{t('kpis:Fields.Required')}</IPLabel>
        </TextInfo>
        <IPCheckbox
          checked={props.kpi.required}
          onChange={(ev, checked) => props.requiredUpdated(checked ?? false)}
        ></IPCheckbox>
      </IPStack.Item>
    </IPStack>
  );
};

//
// Date
//
const KPITypeConfigDate = (props: IKPITypeConfigRendererProps) => {
  const { t } = useTranslation(['translation', 'forms', 'kpi']);

  const getMinVal = (): Date | undefined => {
    if (!props.kpi.defValue && props.kpi.minValue) {
      return new Date(props.kpi.minValue);
    } else if (props.kpi.defValue && props.kpi.minValue) {
      const nrDays = getDateDiffDays(new Date(props.kpi.minValue), new Date(props.kpi.defValue));

      return addDateDays(new Date(), nrDays);
    } else {
      return undefined;
    }
  };

  const getMaxVal = (): Date | undefined => {
    if (!props.kpi.defValue && props.kpi.maxValue) {
      return new Date(props.kpi.maxValue);
    } else if (props.kpi.defValue && props.kpi.maxValue) {
      const nrDays = getDateDiffDays(new Date(props.kpi.maxValue), new Date(props.kpi.defValue));

      return addDateDays(new Date(), nrDays);
    } else {
      return undefined;
    }
  };

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
      <IPStack.Item>
        <TextInfo message={t('kpis:Fields.DefValueDateInfo')}>
          <IPLabel>{t('kpis:Fields.DefValueDate')}</IPLabel>
        </TextInfo>
        <IPCheckbox
          checked={!isEmpty(props.kpi.defValue)}
          onChange={(ev, checked) => props.defValueUpdated(checked === true ? new Date().toISOString() : undefined)}
        ></IPCheckbox>
      </IPStack.Item>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPStack.Item grow>
            <TextInfo
              message={t('kpis:Fields.MinValueInfo', {
                entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
              })}
            >
              <IPLabel>{t('kpis:Fields.MinValue')}</IPLabel>
            </TextInfo>
            <LocalizedDatePicker
              value={getMinVal()}
              minDate={undefined}
              maxDate={
                !isEmpty(props.kpi.defValue)
                  ? new Date()
                  : props.kpi.maxValue
                  ? new Date(props.kpi.maxValue)
                  : undefined
              }
              onDateChange={(newValue) => {
                if (!props.kpi) return;
                props.minValueUpdated(newValue?.toISOString());
              }}
              allowEmptyValue={true}
            />
          </IPStack.Item>
          <IPStack.Item grow>
            <TextInfo
              message={t('kpis:Fields.MaxValueInfo', {
                entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
              })}
            >
              <IPLabel>{t('kpis:Fields.MaxValue')}</IPLabel>
            </TextInfo>
            <LocalizedDatePicker
              value={getMaxVal()}
              minDate={
                !isEmpty(props.kpi.defValue)
                  ? new Date()
                  : props.kpi.minValue
                  ? new Date(props.kpi.minValue)
                  : undefined
              }
              maxDate={undefined}
              onDateChange={(newValue) => {
                if (!props.kpi) return;
                props.maxValueUpdated(newValue?.toISOString());
              }}
              allowEmptyValue={true}
            />
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <TextInfo
          message={t('kpis:Fields.RequiredInfo', {
            entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
          })}
        >
          <IPLabel>{t('kpis:Fields.Required')}</IPLabel>
        </TextInfo>
        <IPCheckbox
          checked={props.kpi.required}
          onChange={(ev, checked) => props.requiredUpdated(checked ?? false)}
        ></IPCheckbox>
      </IPStack.Item>
    </IPStack>
  );
};

//
// Number
//
const KPITypeConfigNumber = (props: IKPITypeConfigRendererProps) => {
  const { t } = useTranslation(['translation', 'forms', 'kpi']);

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
      <IPStack.Item>
        <TextInfo message={t('kpis:Fields.DecimalCountInfo')}>
          <IPLabel>{t('kpis:Fields.DecimalCount')}</IPLabel>
        </TextInfo>
        <IPSpinButton
          value={props.kpi.decimalCount?.toString() ?? ''}
          min={0}
          max={8}
          onChange={(ev, newValue) => props.decimalCountUpdated(Number.parseInt(newValue ?? ''))}
          styles={{ root: { width: 150 } }}
        ></IPSpinButton>
      </IPStack.Item>
      <IPStack.Item>
        <IPStack horizontal wrap tokens={globalStackTokensGapSmall}>
          <IPStack.Item>
            <TextInfo
              message={t('kpis:Fields.MinValueInfo', {
                entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
              })}
            >
              <IPLabel>{t('kpis:Fields.MinValue')}</IPLabel>
            </TextInfo>
            <IPSpinButton
              value={props.kpi.minValue?.toString() ?? ''}
              min={-globalKPIDecimal18c8}
              max={props.kpi.maxValue ? Number.parseFloat(props.kpi.maxValue) : globalKPIDecimal18c8}
              onIncrement={(newValue) => {
                if (!newValue) newValue = '1';
                newValue = (Number.parseFloat(newValue) + 1).toFixed(props.kpi.decimalCount);

                return validateMaxValue(props.kpi, props.kpi.minValue, newValue);
              }}
              onDecrement={(newValue) => {
                if (!newValue) newValue = '1';
                newValue = (Number.parseFloat(newValue) - 1).toFixed(props.kpi.decimalCount);

                return newValue;
              }}
              onValidate={(newValue) => {
                //normally empty values are discarded as not valid
                //also return empty strings to reset the field
                return newValue;
              }}
              onChange={(ev, newValue) => {
                if (newValue) {
                  newValue = validateMaxValue(props.kpi, props.kpi.minValue, newValue);
                  newValue = validateDefMinValue(props.kpi, props.kpi.minValue, newValue);
                  props.minValueUpdated(newValue);
                } else {
                  props.minValueUpdated(undefined);
                }
              }}
              styles={{ root: { width: 150 } }}
            ></IPSpinButton>
          </IPStack.Item>
          <IPStack.Item>
            <TextInfo
              message={t('kpis:Fields.DefValueInfo', {
                entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
              })}
            >
              <IPLabel>{t('kpis:Fields.DefValue')}</IPLabel>
            </TextInfo>
            <IPSpinButton
              value={props.kpi.defValue ?? ''}
              min={props.kpi.minValue ? Number.parseFloat(props.kpi.minValue) : -globalKPIDecimal18c8}
              max={props.kpi.maxValue ? Number.parseFloat(props.kpi.maxValue) : globalKPIDecimal18c8}
              onIncrement={(newValue) => {
                if (!newValue) newValue = '1';
                newValue = (Number.parseFloat(newValue) + 1).toFixed(props.kpi.decimalCount);

                return validateMaxValue(props.kpi, props.kpi.defValue, newValue);
              }}
              onDecrement={(newValue) => {
                if (!newValue) newValue = '1';
                newValue = (Number.parseFloat(newValue) - 1).toFixed(props.kpi.decimalCount);

                return validateMinValue(props.kpi, props.kpi.defValue, newValue);
              }}
              onValidate={(newValue) => {
                //normally empty values are discarded as not valid
                //also return empty strings to reset the field
                return newValue;
              }}
              onChange={(ev, newValue) => {
                newValue = validateMaxValue(props.kpi, props.kpi.defValue, newValue);
                newValue = validateMinValue(props.kpi, props.kpi.defValue, newValue);
                props.defValueUpdated(newValue);
              }}
              styles={{ root: { width: 150 } }}
            ></IPSpinButton>
          </IPStack.Item>
          <IPStack.Item>
            <TextInfo
              message={t('kpis:Fields.MaxValueInfo', {
                entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
              })}
            >
              <IPLabel>{t('kpis:Fields.MaxValue')}</IPLabel>
            </TextInfo>
            <IPSpinButton
              value={props.kpi.maxValue?.toString() ?? ''}
              min={props.kpi.minValue ? Number.parseFloat(props.kpi.minValue) : -globalKPIDecimal18c8}
              max={globalKPIDecimal18c8}
              onIncrement={(newValue) => {
                if (!newValue) newValue = '1';
                newValue = (Number.parseFloat(newValue) + 1).toFixed(props.kpi.decimalCount);

                return newValue;
              }}
              onDecrement={(newValue) => {
                if (!newValue) newValue = '1';
                newValue = (Number.parseFloat(newValue) - 1).toFixed(props.kpi.decimalCount);

                return validateMinValue(props.kpi, props.kpi.maxValue, newValue);
              }}
              onValidate={(newValue) => {
                //normally empty values are discarded as not valid
                //also return empty strings to reset the field
                return newValue;
              }}
              onChange={(ev, newValue) => {
                if (newValue) {
                  newValue = validateMinValue(props.kpi, props.kpi.maxValue, newValue);
                  newValue = validateDefMaxValue(props.kpi, props.kpi.maxValue, newValue);
                  props.maxValueUpdated(newValue);
                } else {
                  props.maxValueUpdated(undefined);
                }
              }}
              styles={{ root: { width: 150 } }}
            ></IPSpinButton>
          </IPStack.Item>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <TextInfo
          message={t('kpis:Fields.RequiredInfo', {
            entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
          })}
        >
          <IPLabel>{t('kpis:Fields.Required')}</IPLabel>
        </TextInfo>
        <IPCheckbox
          checked={props.kpi.required}
          onChange={(ev, checked) => props.requiredUpdated(checked ?? false)}
        ></IPCheckbox>
      </IPStack.Item>
    </IPStack>
  );
};

//
// Success & Error
//
const KPITypeConfigSuccesError = (props: IKPITypeConfigRendererProps) => {
  const { t } = useTranslation(['translation', 'forms', 'kpi']);

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
      <IPStack.Item>
        <TextInfo
          message={t('kpis:Fields.ShowLabelInfo', {
            entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
          })}
        >
          <IPLabel>{t('kpis:Fields.ShowLabel')}</IPLabel>
        </TextInfo>
        <IPCheckbox
          checked={props.kpi.showLabel}
          onChange={(ev, checked) => props.showLabelUpdated(checked ?? true)}
        ></IPCheckbox>
      </IPStack.Item>
      <IPStack.Item>
        <TextInfo message={t('kpis:Fields.QuestionInfo')}>
          <IPLabel>{t('kpis:Fields.Question')}</IPLabel>
        </TextInfo>
        <RichTextEditorMCE
          html={props.kpi.defValue ?? ''}
          maxLength={1024}
          maxHeight={200}
          onChange={(newValue) => {
            if (isEmpty(newValue) && !props.kpi.showLabel) {
              newValue = t('kpis:Fields.QuestionExample');
            }
            props.defValueUpdated(newValue);
          }}
          compactToolbar={true}
          disallowFullscreen={true}
        />
      </IPStack.Item>
      <IPStack.Item>
        <TextInfo
          message={t('kpis:Fields.RequiredInfo', {
            entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
          })}
        >
          <IPLabel>{t('kpis:Fields.Required')}</IPLabel>
        </TextInfo>
        <IPCheckbox
          checked={props.kpi.required}
          onChange={(ev, checked) => props.requiredUpdated(checked ?? false)}
        ></IPCheckbox>
      </IPStack.Item>
    </IPStack>
  );
};

//
// Choice
//
const choicePresetKeys = ['YesNo', 'HighMedLow', 'Scale5'] as const;
type ChoicePresetKey = (typeof choicePresetKeys)[number];

const KPITypeConfigChoice = (props: IKPITypeConfigRendererProps) => {
  const { t } = useTranslation(['translation', 'forms', 'kpi', 'kpis']);
  const [comboxItemList, SetComboxItemList] = useState<ComboBoxItemList>(new ComboBoxItemList());
  const [showQuickAddPresets, setShowQuickAddPresets] = useState<boolean>(true);

  const getChoicePresetValues = (presetKey: ChoicePresetKey): string => {
    switch (presetKey) {
      case 'YesNo':
        return t('kpis:ChoicePresets.YesNo.Values');
      case 'HighMedLow':
        return t('kpis:ChoicePresets.HighMedLow.Values');
      case 'Scale5':
        return t('kpis:ChoicePresets.Scale5.Values');
    }
  };

  const getChoicePresetLabel = (presetKey: ChoicePresetKey): string => {
    switch (presetKey) {
      case 'YesNo':
        return t('kpis:ChoicePresets.YesNo.Label');
      case 'HighMedLow':
        return t('kpis:ChoicePresets.HighMedLow.Label');
      case 'Scale5':
        return t('kpis:ChoicePresets.Scale5.Label');
    }
  };

  useEffect(() => {
    if (props.kpi.choices) {
      const newItems = props.kpi.choices.map((c) => {
        const newItem = new ComboBoxItem();
        newItem.id = newGuid();
        newItem.description = c.value || '?';
        newItem.key = c.stringValueKey || 0;

        return newItem;
      });

      const newList = new ComboBoxItemList();
      newList.items = newItems;

      if (props.kpi.defValue) {
        const defKey = Number.parseInt(props.kpi.defValue);
        newList.default = newItems.find((n) => n.key === defKey);
      }

      SetComboxItemList(newList);
      setShowQuickAddPresets(newItems.length === 0);
    }
  }, [props.kpi]);

  const onUpdateComboBoxList = (newComboBoxList: ComboBoxItemList) => {
    SetComboxItemList(newComboBoxList);
    setShowQuickAddPresets(newComboBoxList.items.length === 0);

    const stringValues = newComboBoxList.items.map((item, index) => {
      const val = new StringValue();
      val.sortOrder = index;
      val.stringTypeId = StringValueTypes.KPIChoice;
      val.stringValueKey = item.key;
      val.stringTypeEntityId = props.kpi.kpiId;
      val.trans = [new StringValue_Translation()];
      val.trans[0].value = item.description;
      val.value = item.description;

      return val;
    });

    props.choicesUpdated(stringValues, newComboBoxList.default?.key.toString());
  };

  // Quick-add preset choice sets: clicking one replaces the current choices with that preset.
  const applyChoicePreset = (presetKey: ChoicePresetKey) => {
    const values = getChoicePresetValues(presetKey).split('|');
    if (values.length === 0) return;

    let nextKey = 1;
    const newItems = values.map((v) => {
      const item = new ComboBoxItem();
      item.id = newGuid();
      item.description = v;
      item.key = nextKey++;

      return item;
    });

    const newList = new ComboBoxItemList();
    newList.items = newItems;
    onUpdateComboBoxList(newList);
  };

  return (
    <IPStack tokens={globalStackTokensGapSmall} styles={{ root: { padding: 10 } }}>
      <IPStack.Item>
        <InlineSuggestions
          items={choicePresetKeys.map((key) => ({
            id: key,
            label: getChoicePresetLabel(key),
            tooltip: getChoicePresetValues(key).replace(/\|/g, ', '),
          }))}
          onSelect={(item) => applyChoicePreset(item.id as ChoicePresetKey)}
          expanded={showQuickAddPresets}
          onExpandedChange={setShowQuickAddPresets}
          text={{
            show: t('kpis:ChoicePresets.QuickAddShow'),
            label: t('kpis:ChoicePresets.QuickAdd'),
            hint: t('kpis:ChoicePresets.QuickAddHint'),
            dismiss: t('kpis:ChoicePresets.QuickAddDismiss'),
          }}
        />
      </IPStack.Item>
      <IPStack.Item grow>
        <IPStack styles={{ root: { height: 300 } }}>
          <ComboBoxItemCreator
            allowEdit={true}
            singleLineEdit={true}
            itemList={comboxItemList}
            onUpdate={onUpdateComboBoxList}
          />
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <TextInfo
          message={t('kpis:Fields.RequiredInfo', {
            entity: props.kpi.isField ? t('kpis:Entity.Field') : t('kpis:Entity.KPI'),
          })}
        >
          <IPLabel>{t('kpis:Fields.Required')}</IPLabel>
        </TextInfo>
        <IPCheckbox
          checked={props.kpi.required}
          onChange={(ev, checked) => props.requiredUpdated(checked ?? false)}
        ></IPCheckbox>
      </IPStack.Item>
    </IPStack>
  );
};
